/* eslint-disable ember/require-tagless-components */
// eslint-disable-next-line ember/no-classic-components
import Component from '@ember/component';
import { action, set } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { Task } from 'ember-concurrency';
import type SubscriptionModel from 'garaje/models/subscription';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';

import type { SupportedAgreements } from '../component';
import type { SaveOptions } from '../edit-text/component';

const RESIGN_DAYS = 30;
const RESIGN_OPTIONS = ['', 'days', 'always', 'global_resign_date'];

export default class ReSignComponent extends Component {
  declare changeset: DetailedChangeset<SupportedAgreements>;
  declare updateAndSaveTask: Task<void, [SaveOptions<keyof SupportedAgreements>]>;
  declare disabled: boolean;
  declare trackLegalDocument: () => Promise<void>;
  declare model: SupportedAgreements;
  declare propagable: boolean;
  declare vrSubscription: SubscriptionModel;

  @service declare state: StateService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked _resignOption: string = this.setupResignOption();
  @tracked defaultDays = RESIGN_DAYS;
  @tracked isEditingResign = false;
  @tracked agreementDurationError = '';

  get isSaveResignDisabled(): boolean {
    const isInvalid = this.changeset.isInvalid;
    const isClean = !this.changeset.isDirty;
    const isRunning = this.updateAndSaveTask.isRunning;
    const noOption = this.resignOption === '';
    const hasError = this.agreementDurationError !== '';
    return isInvalid || isClean || isRunning || noOption || hasError;
  }

  get hasAccessToIncludesNdaFeature(): boolean {
    return Boolean(this.state.features?.includesNda);
  }

  get isResignEnabled(): boolean {
    return this.changeset.duration !== 0 || Boolean(this.changeset.globalResignDate);
  }

  get shouldOpenResignPanel(): boolean {
    return this.isEditingResign || (this.disabled && this.isResignEnabled);
  }

  setupResignOption(): string {
    const days = this.changeset.duration;
    const globalResignDate = this.changeset.globalResignDate;

    if (globalResignDate !== null && globalResignDate !== undefined) {
      return 'global_resign_date';
    }

    return days === null ? 'always' : days === 0 ? '' : 'days';
  }

  applyChangeset(changeset: DetailedChangeset<SupportedAgreements>): void {
    changeset.execute();
    changeset.rollback();
  }

  rollbackChangeset(changeset: DetailedChangeset<SupportedAgreements>): void {
    changeset.rollback();
  }

  isValidResignDays(value: string | number): boolean {
    const days = typeof value === 'string' ? parseInt(value, 10) : value;
    return !isNaN(days) && days > 0;
  }

  @action
  rollbackResignSetting(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.rollbackChangeset(changeset);
    this.resignOption = this.setupResignOption();
    this.defaultDays = RESIGN_DAYS;
    this.agreementDurationError = '';
  }

  @action
  saveResignSetting(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.applyChangeset(changeset);
    void this.updateAndSaveTask.perform({ model: this.model }).then(() => {
      this.isEditingResign = false;
      void this.trackLegalDocument();
    });
  }

  @action
  resignDaysOptionChanged(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.resignOption = 'days';
    set(changeset, 'duration', this.defaultDays);
    set(changeset, 'globalResignDate', null);
    // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions
    scheduleOnce('afterRender', this, () => document.querySelector<HTMLInputElement>('#days-input')?.focus());
  }

  @action
  resignAlwaysOptionChanged(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.resignOption = 'always';
    set(changeset, 'duration', null);
    set(changeset, 'globalResignDate', null);
    if (!this.isValidResignDays(this.resignDays)) {
      this.defaultDays = RESIGN_DAYS;
    }
    this.agreementDurationError = '';
  }

  @action
  resignGlobalResignDateOptionChanged(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.resignOption = 'global_resign_date';
    set(changeset, 'duration', 0);
    set(changeset, 'globalResignDate', new Date()); // initialize to current date
    this.agreementDurationError = '';
  }

  @action
  disableResignSetting(changeset: DetailedChangeset<SupportedAgreements>): void {
    this.resignOption = '';
    this.agreementDurationError = '';
    this.defaultDays = RESIGN_DAYS;
    set(changeset, 'duration', 0);
    set(changeset, 'globalResignDate', null);
    if (this.changeset.isDirty) {
      this.applyChangeset(changeset);
      void this.updateAndSaveTask.perform({ model: this.model, propagable: this.propagable }).then(() => {
        this.isEditingResign = false;
        void this.trackLegalDocument();
      });
    } else {
      this.isEditingResign = false;
    }
  }

  @action
  validateResignDays(days: string): void {
    this.agreementDurationError = this.isValidResignDays(days) ? '' : 'Minimum is 1';
  }

  @action
  updateGlobalResignDate(changeset: DetailedChangeset<SupportedAgreements>, date: Date): void {
    set(changeset, 'globalResignDate', date);
  }

  get resignDays(): string | number {
    const days = this.changeset.duration;

    if (this.resignOption === 'days') {
      return days ?? '';
    }

    return this.defaultDays;
  }

  set resignDays(value: string) {
    let _val: number | null = parseInt(value, 10);
    _val = isNaN(_val) ? null : _val;
    set(this.changeset, 'duration', _val);
    this.defaultDays = _val ? _val : RESIGN_DAYS;
  }

  get resignOption(): string {
    const value = this._resignOption;
    if (RESIGN_OPTIONS.includes(value)) {
      return value;
    } else {
      return this.setupResignOption();
    }
  }

  set resignOption(value: string) {
    if (RESIGN_OPTIONS.includes(value)) {
      this._resignOption = value;
    }
  }

  get globalResignDateEnabled(): boolean {
    return this.featureFlags.isEnabled('feat-resign-on-x-date');
  }

  get globalResignDateValue(): Date {
    return this.changeset.globalResignDate || new Date();
  }
}
